// The live ambient values: the defaults, or, while tuning, the panel's
// overrides kept in this browser's localStorage.
import type { Weekday } from '../../../systems/api/types';
import { createStore } from '../../../systems/store';
import { tuningEnabled } from '../../tuning/tuningGate';
import { AMBIENT_DEFAULTS, type AmbientConfig } from './ambientConfig';

// Only the values that differ from the defaults are kept, so a value nobody
// touched follows the defaults when they change. v1 kept whole snapshots,
// which froze every default; it is ignored.
const KEY = 'wits.sky-tuning.v2';

function load(): AmbientConfig {
  if (!tuningEnabled()) return AMBIENT_DEFAULTS;
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? merge(AMBIENT_DEFAULTS, JSON.parse(raw) as Partial<AmbientConfig>) : AMBIENT_DEFAULTS;
  } catch {
    return AMBIENT_DEFAULTS;
  }
}

/** What differs from base, as a partial of the same shape (arrays whole); undefined when nothing does. */
export function changesFrom(base: unknown, value: unknown): unknown {
  if (typeof base !== 'object' || base === null || Array.isArray(base)) {
    return JSON.stringify(base) === JSON.stringify(value) ? undefined : value;
  }
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(base)) {
    const d = changesFrom((base as Record<string, unknown>)[key], (value as Record<string, unknown>)[key]);
    if (d !== undefined) out[key] = d;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The defaults with the saved changes laid over them; anything the changes lack keeps its default. */
export function merge<T>(base: T, over: unknown): T {
  // A null override is a value (a day without satellites), so only a missing one keeps the base.
  if (Array.isArray(base) || typeof base !== 'object' || base === null) return (over === undefined ? base : over) as T;
  if (typeof over !== 'object' || over === null) return base;
  const out = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(base)) out[key] = merge((base as Record<string, unknown>)[key], (over as Record<string, unknown>)[key]);
  return out as T;
}

export const ambientSettings = createStore<AmbientConfig>(load());

export function setAmbient(next: AmbientConfig): void {
  ambientSettings.set(next);
  try {
    const changes = changesFrom(AMBIENT_DEFAULTS, next);
    if (changes === undefined) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(changes));
  } catch {
    // Private windows may refuse storage; the change still applies.
  }
}

export function resetAmbient(): void {
  ambientSettings.set(AMBIENT_DEFAULTS);
  try {
    localStorage.removeItem(KEY);
  } catch {
    // As above.
  }
}

/** A night to show instead of tonight's, while tuning another day's sky. */
export const weekdayPreview = createStore<Weekday | null>(null);
